/**
 * Pricing Dialog Component
 * Handles creation and editing of pricing rules
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Bed, UtensilsCrossed } from 'lucide-react';
import { getAmenityIcon } from '../constants';

export default function PricingDialog({
  open,
  onOpenChange,
  editingPricing,
  formData,
  setFormData,
  onSubmit,
  hotelRoomTypes,
  activeBedTypes,
  activeAmenities
}) {
  const handleBedTypeToggle = (bedTypeId) => {
    setFormData(prev => ({
      ...prev,
      bed_type_ids: prev.bed_type_ids.includes(bedTypeId)
        ? prev.bed_type_ids.filter(id => id !== bedTypeId)
        : [...prev.bed_type_ids, bedTypeId]
    }));
  };

  const handleAmenityToggle = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenity_ids: prev.amenity_ids.includes(amenityId)
        ? prev.amenity_ids.filter(id => id !== amenityId)
        : [...prev.amenity_ids, amenityId]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingPricing ? 'Edit Pricing Rule' : 'Add Pricing Rule'}</DialogTitle>
          <DialogDescription>Configure room pricing with amenities and meal options</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Rule Name *</Label>
            <Input 
              value={formData.name} 
              onChange={(e) => setFormData({...formData, name: e.target.value})} 
              placeholder="e.g., Deluxe King with Breakfast" 
              required 
              className="border-orange-200" 
            />
          </div>
          
          <div className="space-y-2">
            <Label>Room Type</Label>
            <Select 
              value={formData.room_type_id || 'any'} 
              onValueChange={(value) => setFormData({...formData, room_type_id: value === 'any' ? '' : value})}
            >
              <SelectTrigger className="border-orange-200">
                <SelectValue placeholder="Any type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any Type</SelectItem>
                {hotelRoomTypes.map((rt) => (
                  <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hotelRoomTypes.length === 0 && (
              <p className="text-xs text-amber-600">No room types assigned to this hotel. Contact Company Admin to assign room types.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Bed Types (select multiple)</Label>
            <ScrollArea className="h-20 border border-orange-200 rounded-lg p-2">
              <div className="flex flex-wrap gap-2">
                {activeBedTypes.map((bt) => {
                  const isSelected = formData.bed_type_ids.includes(bt.id);
                  return (
                    <label 
                      key={bt.id} 
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg cursor-pointer text-xs ${
                        isSelected 
                          ? 'bg-orange-100 border border-orange-400' 
                          : 'bg-gray-50 border border-transparent hover:bg-orange-50'
                      }`}
                    >
                      <input 
                        type="checkbox" 
                        checked={isSelected} 
                        onChange={() => handleBedTypeToggle(bt.id)} 
                        className="hidden" 
                      />
                      <Bed className="h-3 w-3" />
                      {bt.name}
                    </label>
                  );
                })}
              </div>
            </ScrollArea>
            <p className="text-xs text-gray-400">Leave empty to apply to any bed type</p>
          </div>

          <div className="space-y-2">
            <Label>Amenities Included</Label>
            <ScrollArea className="h-24 border border-orange-200 rounded-lg p-2">
              <div className="flex flex-wrap gap-2">
                {activeAmenities.map((amenity) => {
                  const Icon = getAmenityIcon(amenity.icon);
                  const isSelected = formData.amenity_ids.includes(amenity.id);
                  return (
                    <label 
                      key={amenity.id} 
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg cursor-pointer text-xs ${
                        isSelected 
                          ? 'bg-orange-100 border border-orange-400' 
                          : 'bg-gray-50 border border-transparent hover:bg-orange-50'
                      }`}
                    >
                      <input 
                        type="checkbox" 
                        checked={isSelected} 
                        onChange={() => handleAmenityToggle(amenity.id)} 
                        className="hidden" 
                      />
                      <Icon className="h-3 w-3" />
                      {amenity.name}
                    </label>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          <div className="space-y-2">
            <Label>Base Price (per night) *</Label>
            <Input 
              type="number" 
              value={formData.base_price} 
              onChange={(e) => setFormData({...formData, base_price: e.target.value})} 
              required 
              className="border-orange-200" 
              placeholder="0" 
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <UtensilsCrossed className="h-4 w-4 text-orange-500" />
              Meal Plan Prices (per person/day)
            </Label>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Breakfast</Label>
                <Input 
                  type="number" 
                  value={formData.breakfast_price} 
                  onChange={(e) => setFormData({...formData, breakfast_price: e.target.value})} 
                  className="border-orange-200" 
                  placeholder="0" 
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Lunch</Label>
                <Input 
                  type="number" 
                  value={formData.lunch_price} 
                  onChange={(e) => setFormData({...formData, lunch_price: e.target.value})} 
                  className="border-orange-200" 
                  placeholder="0" 
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Dinner</Label>
                <Input 
                  type="number" 
                  value={formData.dinner_price} 
                  onChange={(e) => setFormData({...formData, dinner_price: e.target.value})} 
                  className="border-orange-200" 
                  placeholder="0" 
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch 
              checked={formData.is_active} 
              onCheckedChange={(checked) => setFormData({...formData, is_active: checked})} 
            />
            <Label>Active</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-gradient-orange text-white">
              {editingPricing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
